import { ThunkDispatch } from "core-integration/src/store";
import {
  getElementTypes,
  getElementTypesById,
} from "core-integration/src/store/element-types/reducers";
import { getElementControls } from "core-integration/src/store/elements/reducers";
import {
  showElementSummary,
  updateElementControlsAndMaxCost,
  updateElementTypeControl,
} from "core-integration/src/store/elements/thunks";
import {
  IElement,
  IElementControls,
} from "core-integration/src/store/elements/types";
import { getRules } from "core-integration/src/store/game/reducers";
import {
  getAffordableElementsFromControls,
  getProposedElementsById,
  getToSpend,
  getTypesNeeded,
} from "core-integration/src/store/squad/reducers";
import {
  proposeElement,
  removeElement,
} from "core-integration/src/store/squad/thunks";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { integerToMoney } from "core-integration/src/utils/money";
import groupBy from "lodash/groupBy";
import * as React from "react";
import { useEffect, useState } from "react";
import { Trans, withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "rebass/styled-components";
import styled, { css } from "styled-components";
import { getStatAbbrev, getStatDetails } from "../../utils/stats";
import Alert from "../Alert";
import Button from "../Button";
import Dialog, { DialogButtonItem } from "../Dialog";
import DialogHeading from "../DialogHeading";
import Paginator from "../element-controls/Paginator";
import ElementListRow from "../ElementListRow";
import { ElementTable } from "../ElementTable";
import { ControlArrowLeft } from "../icons/Arrows";
import Panel from "../Panel";
import Tooltip, { TooltipLabel } from "../Tooltip";
import ElementSearchField from "./ElementSearchField";
import FilterChipRow, {
  defaultElementFilters,
} from "./SquadSelectionFilters/FilterChipRow";

const PanelDescription = styled.p`
  margin: 0;
  padding: 0 ${({ theme }) => theme.space[2]};
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes[2]};
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space[2]};
  padding: ${({ theme }) => theme.space[2]};
`;

const ElementAddRemove = styled.th`
  width: 10%;
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    display: none;
  }
`;

const ElementsBudgetWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 ${({ theme }) => theme.space[2]};
`;

const BudgetContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space[2]};
  margin: ${({ theme }) => theme.space[2]} 0;

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    display: none;
  }
`;

const BudgetTitle = styled.span`
  color: ${({ theme }) => theme.colors.white};
  text-transform: capitalize;
`;

const BudgetValue = styled.span<{ $value: number }>`
  color: ${({ $value, theme }) =>
    $value < 0 ? theme.colors.white : theme.colors.green};
  font-weight: bold;
`;

const ElementsShown = styled.p`
  margin: ${({ theme }) => theme.space[2]} 0;
  text-align: center;

  strong {
    color: ${({ theme }) => theme.colors.white};
  }
`;

const ElementTableHeadStyles = css`
  &&& {
    background-color: ${({ theme }) => theme.colors.grey24};
  }
`;

const ElementListBlank = styled.th`
  ${ElementTableHeadStyles}
`;

const ElementListElement = styled.th`
  ${ElementTableHeadStyles}
  width: 46%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 45%;
  }
`;

const ElementListPrice = styled.th`
  ${ElementTableHeadStyles}
  width: 22%;
  font-size: ${({ theme }) => theme.fontSizes[1]};

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 25%;
  }
`;

const ElementListStat = styled.th`
  ${ElementTableHeadStyles}
  width: 15%;
  font-size: ${({ theme }) => theme.fontSizes[1]};

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    width: 22%;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 18%;
  }
`;

const ElementTypeButton = styled.span`
  padding: 0;
  border: 0;
  background-color: transparent;
  color: currentColor;
  font-size: ${({ theme }) => theme.fontSizes[1]};
  cursor: pointer;
  font-family: ${({ theme }) => theme.fonts.base};
`;

const ButtonWrap = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    display: none;
  }
`;

const PlayerActions = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    margin: 0 auto;
  }
`;

interface IOwnProps {
  hideRef: React.RefObject<HTMLButtonElement>;
  hideSidebar: () => void;
}

type Props = IOwnProps & WithTranslation;

const ElementList = ({ hideRef, hideSidebar, t, i18n }: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const [elementForMenu, setElementForMenu] = useState<IElement | null>(null);
  const [page, setPage] = useState<number>(1);

  const controls = useSelector(getElementControls);
  const rules = useSelector(getRules);
  const currencyDivisor = 10;
  const elementTypes = useSelector(getElementTypes);
  const elementTypesById = useSelector(getElementTypesById);
  const elements = useSelector(getAffordableElementsFromControls);
  const proposedElementsById = useSelector(getProposedElementsById);
  const teamsById = useSelector(getTeamsById);
  const typesNeeded = useSelector(getTypesNeeded);
  const toSpend = useSelector(getToSpend);
  const statLabel = getStatDetails(String(controls.sort), t)?.label ?? "";
  const statAbbrev = getStatAbbrev(statLabel, i18n.language);

  const updateControlsAndMaxCost = (controls: IElementControls) =>
    dispatch(updateElementControlsAndMaxCost(controls));

  const handleAddElement = (elementId: number) => {
    dispatch(proposeElement(elementId));
    hideSidebar();
    handleHideMenuForElement();
  };

  const handleRemoveElement = (position: number) => {
    dispatch(removeElement(position));
    handleHideMenuForElement();
  };

  const handleShowMenuForElement = (element: IElement) =>
    setElementForMenu(element);

  const handleHideMenuForElement = () => {
    setElementForMenu(null);
  };

  const showDialog = (element: IElement) => {
    dispatch(showElementSummary(element.id));
    handleHideMenuForElement();
  };

  useEffect(() => {
    updateControlsAndMaxCost({
      ...controls,
      ...defaultElementFilters,
      search: "",
      getUnavailable: false,
    });
  }, []);

  useEffect(() => {
    setPage(1);
  }, [controls]);

  const paginateAndGroup = (elements: IElement[], pageSize: number) => {
    const start = (page - 1) * pageSize;
    return {
      data: groupBy(elements.slice(start, start + pageSize), "element_type"),
      totalPages: Math.ceil(elements.length / pageSize),
    };
  };

  const renderMenu = () => {
    const element = elementForMenu;

    if (!element) {
      return null;
    }

    const proposedElement = proposedElementsById[element.id];
    const roomInSquad = typesNeeded[element.element_type];

    let alert = null;
    if (!roomInSquad) {
      alert = (
        <Alert type="error" noRadius>
          {t(
            "elementList.errors.maxElementType",
            "You already have the maximum number of {{ position }}",
            {
              position: elementTypesById[element.element_type].plural_name,
            },
          )}
        </Alert>
      );
    } else if (proposedElement) {
      alert = (
        <Alert type="warning" noRadius>
          <Trans
            i18nKey="elementList.errors.alreadySelected"
            values={{ playerName: element.web_name }}
          >
            <strong>{element.web_name}</strong> is already in your squad.
          </Trans>
        </Alert>
      );
    }

    return (
      <Dialog closeDialog={handleHideMenuForElement}>
        <Dialog.Header closeDialog={handleHideMenuForElement}>
          <Box py={2} px={3}>
            <DialogHeading>
              {`${element.first_name} ${element.second_name}`}
            </DialogHeading>
          </Box>
        </Dialog.Header>
        {alert && alert}

        <Dialog.Body>
          <PlayerActions>
            <DialogButtonItem>
              {proposedElement ? (
                <Button
                  variant="primary"
                  onClick={() => handleRemoveElement(proposedElement.position)}
                  width={1}
                >
                  {t("elementList.removePlayer", "Remove player")}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => handleAddElement(element.id)}
                  disabled={Boolean(!roomInSquad)}
                  width={1}
                >
                  {t("elementList.addPlayer", "Add player")}
                </Button>
              )}
            </DialogButtonItem>
            <DialogButtonItem>
              <Button
                variant="primary"
                onClick={() => showDialog(element)}
                width={1}
              >
                {t("viewInformation", "View information")}
              </Button>
            </DialogButtonItem>
          </PlayerActions>
        </Dialog.Body>
      </Dialog>
    );
  };

  const { data, totalPages } = paginateAndGroup(elements.data, 19);
  return (
    <Panel>
      <Panel.Header
        title={t("elementList.playerSelection", "Player Selection")}
      />
      {rules && (
        <PanelDescription>
          {t(
            "createSquad.help",
            "Select a maximum of {{ teamLimit }} players from a single team or 'Auto Pick' if you're short of time.",
            { teamLimit: rules.squad_team_limit },
          )}
        </PanelDescription>
      )}
      <ControlsRow>
        <ButtonWrap>
          <Button
            size="sm"
            ref={hideRef}
            onClick={hideSidebar}
            startIcon={<ControlArrowLeft />}
          >
            {t("back", "Back")}
          </Button>
        </ButtonWrap>
        <ElementSearchField />
      </ControlsRow>
      <FilterChipRow />
      <ElementsBudgetWrap>
        <ElementsShown>
          <Trans
            i18nKey="elementList.playersShown"
            values={{ length: elements.data?.length }}
          >
            <strong>{`${elements.data.length}`}</strong> players shown
          </Trans>
        </ElementsShown>
        <BudgetContainer>
          <BudgetTitle>{t("elementList.budget", "Budget")}:</BudgetTitle>
          <BudgetValue $value={toSpend}>
            {integerToMoney(toSpend, currencyDivisor)}
          </BudgetValue>
        </BudgetContainer>
      </ElementsBudgetWrap>
      {elementTypes.map(
        (et) =>
          data[et.id] && (
            <ElementTable key={et.id} elementType={et.id}>
              <thead>
                <tr>
                  <ElementListBlank>&nbsp;</ElementListBlank>
                  <ElementListElement>
                    <ElementTypeButton
                      onClick={() => dispatch(updateElementTypeControl(et.id))}
                    >
                      {et.plural_name}
                    </ElementTypeButton>
                  </ElementListElement>
                  <ElementListPrice>
                    <Tooltip content={t("price", "Price in Million SAR")}>
                      <TooltipLabel>M SAR</TooltipLabel>
                    </Tooltip>
                  </ElementListPrice>
                  <ElementListStat>
                    <Tooltip content={statLabel}>
                      <TooltipLabel>{statAbbrev}</TooltipLabel>
                    </Tooltip>
                  </ElementListStat>
                  <ElementAddRemove>&nbsp;</ElementAddRemove>
                </tr>
              </thead>
              <tbody>
                {data[et.id].map((e) => (
                  <ElementListRow
                    key={e.id}
                    element={e}
                    hasRoomInPosition={Boolean(typesNeeded[e.element_type])}
                    proposedElementsById={proposedElementsById}
                    proposedPosition={proposedElementsById[e.id]?.position}
                    isProposed={proposedElementsById[e.id] ? true : false}
                    handleRemoveElement={(elementPosition) =>
                      handleRemoveElement(elementPosition)
                    }
                    handleAddElement={(elementId) =>
                      handleAddElement(elementId)
                    }
                    renderElementMenu={() => handleShowMenuForElement(e)}
                    renderElementDialog={() => showDialog(e)}
                    team={teamsById[e.team]}
                    currencyDivisor={currencyDivisor}
                    sort={controls.sort}
                  />
                ))}
              </tbody>
            </ElementTable>
          ),
      )}
      {renderMenu()}
      <Paginator totalPages={totalPages} page={page} setPage={setPage} />
    </Panel>
  );
};

export default withTranslation()(ElementList);
